import type { FC } from 'react';
import classNames from 'classnames';
import { Button, Tag } from '@carbon/react';
import {
  Archive,
  CheckmarkFilled,
  CheckmarkOutline,
  Misuse,
  MisuseOutline,
  Renew,
  Rocket,
} from '@carbon/react/icons';
import { WorkspaceShellHeader } from 'components/workspace-shell-header';
import type { AreaVerdict, AreaVerdictKind } from 'types/area-workspace';
import { AreaCompareButton } from './area-compare-sheet';
import styles from './area-workspace-header.module.scss';

interface AreaWorkspaceHeaderProps {
  /** The workspace's name, shown as the title. */
  name: string;
  /** The area under review. */
  subtitle?: string;
  // Whether this workspace has been published. Promoting freezes it, so this also
  // decides whether anything here can still be changed.
  promoted: boolean;
  // Whether the area is in play. An archived area can't be archived again, so the
  // slot offers the way back instead.
  active: boolean;
  /** True while a lifecycle write is in flight. */
  busy: boolean;
  onBack: () => void;
  onPromote: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  /** The workspace's draft, which Compare reads. */
  areaGeometryId: number;
  /**
   * The workspace's verdict on the area's shape. Undefined when there is nothing to
   * mark (no freeze item), which leaves the two buttons out.
   */
  verdict?: AreaVerdict | null;
  /** True while a verdict is in flight, or when the workspace may no longer set one. */
  verdictDisabled?: boolean;
  /** Opens the reason panel for a verdict the area does not already carry. */
  onSetVerdict?: (verdict: AreaVerdictKind) => void;
  /** Clears the verdict the area already carries, with no reason asked. */
  onClearVerdict?: () => void;
}

// The workspace bar: what you are in, and the two lifecycle actions that leave it.
// Promotion is one-way, so a promoted workspace reads Read-only and offers a disabled
// Promoted rather than an inverse action.
export const AreaWorkspaceHeader: FC<AreaWorkspaceHeaderProps> = ({
  name,
  subtitle,
  promoted,
  active,
  busy,
  onBack,
  onPromote,
  onArchive,
  onUnarchive,
  areaGeometryId,
  verdict,
  verdictDisabled = false,
  onSetVerdict,
  onClearVerdict,
}) => {
  /**
   * Clicking the verdict the area already carries clears it; the other one asks why.
   * @param {AreaVerdictKind} next The verdict that was clicked.
   */
  const choose = (next: AreaVerdictKind) => {
    if (verdict === next) onClearVerdict?.();
    else onSetVerdict?.(next);
  };

  return (
    <WorkspaceShellHeader
      title={name}
      subtitle={
        <>
          {subtitle}
          {/* Beside the name, where the mock puts it: it describes the workspace you are
              looking at, not an action you can take. */}
          {promoted && (
            <Tag type="green" size="sm" className={styles.readOnly}>
              Read-only
            </Tag>
          )}
        </>
      }
      backLabel="Back to area details"
      onBack={onBack}
      actions={
        promoted ? (
          <Button
            kind="primary"
            size="md"
            renderIcon={Rocket}
            disabled
            title="This workspace has already been promoted to the Area."
          >
            Promoted
          </Button>
        ) : (
          <>
            {/* What the workspace changes against the published area, as the Well
                Workspace bar offers it. Once promoted the draft is the published
                version, so there is nothing left to compare. */}
            <AreaCompareButton
              areaGeometryId={areaGeometryId}
              workspaceName={name}
              areaName={subtitle ?? ''}
            />
            {/* Archive is an edit of the area, so it lives with the workspace's other
                edits rather than on the area page. On an area already archived the
                slot inverts: what's left to offer is the way back into play. */}
            {active ? (
              <Button
                kind="ghost"
                size="md"
                renderIcon={Archive}
                disabled={busy}
                className={styles.link}
                onClick={onArchive}
              >
                Archive
              </Button>
            ) : (
              <Button
                kind="ghost"
                size="md"
                renderIcon={Renew}
                disabled={busy}
                className={styles.link}
                title="Restore this archived area to active"
                onClick={onUnarchive}
              >
                Unarchive
              </Button>
            )}
            {/* The verdict pair, drawn as the design system's Verification status:
                outline icons until set, then Verified's filled check in Blue 60 or
                Incorrect's misuse in Red 60. Correct first, then incorrect. */}
            {verdict !== undefined && (
              <>
                <Button
                  kind="ghost"
                  size="md"
                  renderIcon={
                    verdict === 'correct' ? CheckmarkFilled : CheckmarkOutline
                  }
                  disabled={verdictDisabled}
                  className={classNames(styles.link, {
                    [styles.verified]: verdict === 'correct',
                  })}
                  title={
                    verdict === 'correct'
                      ? 'Remove Verified status'
                      : 'Mark verified to block automatic GIS updates once promoted'
                  }
                  onClick={() => choose('correct')}
                >
                  Verified
                </Button>
                <Button
                  kind="ghost"
                  size="md"
                  renderIcon={verdict === 'incorrect' ? Misuse : MisuseOutline}
                  disabled={verdictDisabled}
                  className={classNames(styles.link, {
                    [styles.incorrect]: verdict === 'incorrect',
                  })}
                  title={
                    verdict === 'incorrect'
                      ? 'Remove Incorrect status'
                      : 'Mark incorrect'
                  }
                  onClick={() => choose('incorrect')}
                >
                  Incorrect
                </Button>
              </>
            )}
            <Button
              kind="primary"
              size="md"
              renderIcon={Rocket}
              disabled={busy}
              title="Publish this workspace as the area's current version"
              onClick={onPromote}
            >
              Promote
            </Button>
          </>
        )
      }
    />
  );
};
